import { Select } from "antd";
import { useMemo } from "react";
import { useCountries } from "../../../hooks/queries/useCountries";
import styled from "styled-components";

export interface LocationControlsProps {
  country: string | null | undefined;
  region: string | null | undefined;
  onCountryChange: (v: any) => void;
  onRegionChange: (v: any) => void;
}

// Replace LocationSpace with a simple flex row that has gap: 8px
const Row = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`;

// Make each select fill available space and apply requested colors:
// - background: #393e46
// - text/icon colors: #eeeeee (same as your color picker trigger in index.css)
const RegionAndCountrySelect = styled(Select)`
  flex: 1;
  width: 100%;

  /* Control */
  .ant-select-selector {
    background: #393e46 !important;
    border-color: #222831 !important;
    color: #eeeeee !important;
  }

  /* Placeholder/text inside */
  .ant-select-selection-placeholder {
    color: #eeeeee !important;
    opacity: 0.8;
  }
  .ant-select-selection-item {
    color: #eeeeee !important;
  }

  /* Icons (suffix/clear) */
  .ant-select-arrow svg,
  .ant-select-clear .anticon svg {
    fill: #eeeeee;
    color: #eeeeee;
  }

  /* Hover/focus */
  &:hover .ant-select-selector {
    border-color: #8e1616 !important;
  }
  &.ant-select-focused .ant-select-selector {
    border-color: #8e1616 !important;
    box-shadow: 0 0 0 2px rgba(142, 22, 22, 0.2);
  }

  /* Dropdown menu */
  .ant-select-dropdown {
    background: #393e46;
    color: #eeeeee;
    border: 1px solid #222831;
  }
  .ant-select-item {
    color: #eeeeee;
  }
  .ant-select-item-option-active,
  .ant-select-item-option-selected {
    background: #222831;
    color: #eeeeee;
  }
`;

export default function LocationControls({
  country,
  region,
  onCountryChange,
  onRegionChange,
}: LocationControlsProps) {
  const { data: countries, isLoading } = useCountries();

  const countryOptions = useMemo(
    () =>
      (countries ?? []).map((c) => ({ value: c.country, label: c.country })),
    [countries],
  );
  const regionsForCountry =
    (countries ?? []).find((c) => c.country === country)?.regions ?? [];
  const regionOptions = useMemo(
    () => regionsForCountry.map((r) => ({ value: r, label: r })),
    [regionsForCountry],
  );

  return (
    <Row>
      <RegionAndCountrySelect
        placeholder="Country"
        loading={isLoading}
        value={country ?? "United States"}
        onChange={(v) => {
          onCountryChange(v);
          const firstRegion =
            (countries ?? []).find((c) => c.country === v)?.regions?.[0] ??
            null;
          onRegionChange(firstRegion);
        }}
        options={countryOptions}
        showSearch
        filterOption={(input, option) =>
          (option?.label as string).toLowerCase().includes(input.toLowerCase())
        }
      />
      <RegionAndCountrySelect
        placeholder="Region"
        loading={isLoading}
        value={region ?? "Oregon"}
        onChange={(v) => onRegionChange(v)}
        options={regionOptions}
        disabled={!country || regionOptions.length === 0}
        showSearch
        filterOption={(input, option) =>
          (option?.label as string).toLowerCase().includes(input.toLowerCase())
        }
      />
    </Row>
  );
}
